// AssignmentPage.jsx
import React, {
    useState,
    useEffect,
    useMemo,
    useCallback,
} from 'react';
import {
    Box,
    Typography,
    Container,
    FormControlLabel,
    Switch,
} from '@mui/material';
import Layout from '../../Layout';
import api from '../../api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/userContext';

import AssignmentFilters from './AssignmentFilters';
import AssignmentActionsBar from './AssignmentActionsBar';
import TrackersTable from './TrackersTable';
import PaginationControls from './PaginationControls';
import AssignmentUpdateBox from './AssignmentUpdateBox';
import ChangedTrackersDialog from './ChangedTrackersDialog';
import {
    isAdminRole,
    isLockedTrackerStatus,
} from '../../utils/trackerStatusLock';

const ROLE_OPTIONS = ['Senior manager', 'Admin', 'Owner', 'Manager'];

const PERIODICITY_OPTIONS = [
    'Annual',
    'Weekly',
    'Event based',
    'One time',
    'All time',
    'Half-yearly',
    'Monthly',
    'Quarterly',
    'Fortnightly',
    'Daily',
    'Bi-Weekly',
];

const AssignmentPage = () => {
    // View state: Basic or Advanced
    const [view, setView] = useState('Basic');

    // Filter states
    const [company, setCompany] = useState('');
    const [location, setLocation] = useState([]); // multi-select: array
    const [user, setUser] = useState([]); // multi-select: array

    // New filter states
    const [lawAreas, setLawAreas] = useState([]);         // options
    const [selectedLawAreas, setSelectedLawAreas] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [selectedPeriodicities, setSelectedPeriodicities] = useState([]);
    const [category, setCategory] = useState('');
    const [activity, setActivity] = useState('');
    const [actOrRule, setActOrRule] = useState('');
    const [startDueDate, setStartDueDate] = useState('');
    const [endDueDate, setEndDueDate] = useState('');
    const [includeClosed, setIncludeClosed] = useState(false);

    // Table and data states
    const [rows, setRows] = useState([]); // all trackers from API
    const [selectedTrackers, setSelectedTrackers] = useState([]); // track selected row IDs
    const [state] = useAuth();
    const isAdmin = isAdminRole(state?.user?.role);

    // Company-related data
    const [companies, setCompanies] = useState([]);
    const [locations, setLocations] = useState([]);
    const [users, setUsers] = useState([]);
    const [companyId, setCompanyId] = useState('');

    // New states for Action/Role/User in the "assignment box"
    const [action, setAction] = useState('Assign');
    const [role, setRole] = useState('Owner');
    const [assignUsers, setAssignUsers] = useState([]);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // ROLLBACK STATE
    const [operationId, setOperationId] = useState(null);
    const [changedTrackers, setChangedTrackers] = useState([]); // after assignment
    const [showChanges, setShowChanges] = useState(false); // dialog
    const [rollbackSelection, setRollbackSelection] = useState([]); // selected UINs
    const isEditableRowForCurrentUser = useCallback(
        (row) => isAdmin || !isLockedTrackerStatus(row?.status),
        [isAdmin]
    );

    // =========================
    // Handlers (memoized)
    // =========================
    const handleCompanyChange = useCallback(
        (event) => setCompany(event.target.value),
        []
    );

    const handleLocationChange = useCallback((event) => {
        const { value } = event.target;
        setLocation(typeof value === 'string' ? value.split(',') : value);
    }, []);

    const handleUserChange = useCallback((event) => {
        const { value } = event.target;
        setUser(typeof value === 'string' ? value.split(',') : value);
    }, []);

    const handleViewChange = useCallback(() => {
        setView((prev) => (prev === 'Basic' ? 'Advanced' : 'Basic'));
    }, []);

    const handleLawAreaChange = useCallback(
        (event) => {
            const { value } = event.target;
            let newValue =
                typeof value === 'string' ? value.split(',') : value;

            // Special "All" option represented by '__ALL__'
            if (newValue.includes('__ALL__')) {
                if (
                    selectedLawAreas.length === lawAreas.length &&
                    lawAreas.length > 0
                ) {
                    // If already all selected → clear
                    newValue = [];
                } else {
                    // Select every law area
                    newValue = lawAreas;
                }
            }

            setSelectedLawAreas(newValue);
        },
        [lawAreas, selectedLawAreas.length]
    );

    const handleRolesFilterChange = useCallback((event) => {
        const { value } = event.target;
        setSelectedRoles(typeof value === 'string' ? value.split(',') : value);
    }, []);

    const handlePeriodicitiesChange = useCallback((event) => {
        const { value } = event.target;
        setSelectedPeriodicities(
            typeof value === 'string' ? value.split(',') : value
        );
    }, []);

    const handleIncludeClosedChange = useCallback((event) => {
        setIncludeClosed(event.target.checked);
    }, []);

    // Fetch basic company info
    useEffect(() => {
        const fetchInfoAndUsers = async () => {
            try {
                const { data } = await api.get('detail/userinfo?view=meta');
                // companyDetails._id might already be an ObjectId, but your earlier code had _id._id
                const compId =
                    data.companyDetails._id._id || data.companyDetails._id;

                setCompanyId(compId);
                setCompanies([data.companyDetails.company_name]);

                // your existing shape: locations is array of arrays, flatten
                if (Array.isArray(data.companyDetails.locations)) {
                    setLocations(data.companyDetails.locations.flat());
                }

                setCompany(data.companyDetails.company_name);
            } catch (error) {
                console.error('Error fetching company info:', error);
            }
        };

        fetchInfoAndUsers();
    }, []);

    // Fetch user options based on companyId
    useEffect(() => {
        const fetchUsers = async () => {
            if (companyId) {
                try {
                    const { data } = await api.post(
                        'company-detail/getUsers',
                        {
                            company: companyId,
                        }
                    );
                    // Assuming backend returns array of emails / strings
                    setUsers(data || []);
                } catch (error) {
                    console.error('Error fetching users:', error);
                }
            }
        };
        fetchUsers();
    }, [companyId]);

    // Fetch law areas for filter
    useEffect(() => {
        const fetchLawAreas = async () => {
            try {
                const response = await api.get('tracker/law-area');
                if (
                    response.data &&
                    Array.isArray(response.data.lawareas)
                ) {
                    const lawAreaArray = response.data.lawareas.map(
                        (lawArea) => lawArea.law_area
                    );
                    setLawAreas(lawAreaArray);
                } else {
                    console.error(
                        'Unexpected law-area response structure:',
                        response.data
                    );
                }
            } catch (error) {
                console.error('Error fetching law areas:', error);
            }
        };

        fetchLawAreas();
    }, []);

    // Submit (fetch trackers)
    const handleSubmit = useCallback(async () => {
        try {
            const payload = {
                locations: location,
                users: user,
                company: companyId,
                lawAreas: selectedLawAreas,
                periodicities: selectedPeriodicities,
                category,
                activity,
                actOrRule,
                startDueDate,
                endDueDate,
                roles: selectedRoles,
                includeClosed,
            };

            const response = await api.post(
                'tracker/assigned-trackers',
                payload
            );

            setRows(response.data || []);
            setCurrentPage(1);
            setSelectedTrackers([]);
        } catch (error) {
            console.error('Error fetching trackers:', error);
            toast.error('Error fetching trackers');
        }
    }, [
        companyId,
        location,
        user,
        selectedLawAreas,
        selectedPeriodicities,
        category,
        activity,
        actOrRule,
        startDueDate,
        endDueDate,
        selectedRoles,
        includeClosed,
    ]);

    // Clear filters and data
    const handleClear = useCallback(() => {
        setCompany('');
        setLocation([]);
        setUser([]);

        setSelectedLawAreas([]);
        setSelectedRoles([]);
        setSelectedPeriodicities([]);
        setCategory('');
        setActivity('');
        setActOrRule('');
        setStartDueDate('');
        setEndDueDate('');
        setIncludeClosed(false);

        setRows([]);
        setSelectedTrackers([]);
        setCurrentPage(1);
    }, []);

    const handleReload = useCallback(() => {
        // reuse same filter set
        handleSubmit();
    }, [handleSubmit]);

    // Row selection
    const handleRowSelect = useCallback((rowId) => {
        const row = rows.find(
            (r) => (r._id || r.uniqueIdentifier) === rowId
        );
        if (!row) return;
        if (!isEditableRowForCurrentUser(row)) {
            toast.info('Locked trackers can only be reassigned by admins.');
            return;
        }
        setSelectedTrackers((prevSelected) => {
            if (prevSelected.includes(rowId)) {
                return prevSelected.filter((id) => id !== rowId);
            }
            return [...prevSelected, rowId];
        });
    }, [rows, isEditableRowForCurrentUser]);

    // Select all across current dataset
    const handleSelectAll = useCallback(() => {
        const selectableIds = rows
            .filter(isEditableRowForCurrentUser)
            .map((r) => r._id || r.uniqueIdentifier);

        setSelectedTrackers((prevSelected) => {
            const selectedSelectableCount = prevSelected.filter((id) =>
                selectableIds.includes(id)
            ).length;

            if (
                selectableIds.length > 0 &&
                selectedSelectableCount === selectableIds.length
            ) {
                return prevSelected.filter((id) => !selectableIds.includes(id));
            }
            return [...new Set([...prevSelected, ...selectableIds])];
        });
    }, [rows, isEditableRowForCurrentUser]);

    // Pagination calculations (memoized)
    const { displayedRows, totalPages } = useMemo(() => {
        const indexOfLastRow = currentPage * rowsPerPage;
        const indexOfFirstRow = indexOfLastRow - rowsPerPage;
        const sliced = rows.slice(indexOfFirstRow, indexOfLastRow);
        const pages = Math.ceil(rows.length / rowsPerPage) || 1;

        return { displayedRows: sliced, totalPages: pages };
    }, [rows, currentPage, rowsPerPage]);

    const selectableRowIds = useMemo(
        () =>
            rows
                .filter(isEditableRowForCurrentUser)
                .map((r) => r._id || r.uniqueIdentifier),
        [rows, isEditableRowForCurrentUser]
    );
    const selectedSelectableCount = useMemo(
        () =>
            selectedTrackers.filter((id) =>
                selectableRowIds.includes(id)
            ).length,
        [selectedTrackers, selectableRowIds]
    );

    const allSelected = useMemo(
        () =>
            selectableRowIds.length > 0 &&
            selectedSelectableCount === selectableRowIds.length,
        [selectableRowIds.length, selectedSelectableCount]
    );

    const someSelected = useMemo(
        () =>
            selectedSelectableCount > 0 &&
            selectedSelectableCount < selectableRowIds.length,
        [selectedSelectableCount, selectableRowIds.length]
    );

    useEffect(() => {
        if (isAdmin) return;
        setSelectedTrackers((prevSelected) =>
            prevSelected.filter((id) => {
                const row = rows.find(
                    (r) => (r._id || r.uniqueIdentifier) === id
                );
                return row ? !isLockedTrackerStatus(row.status) : false;
            })
        );
    }, [rows, isAdmin]);

    // Export as CSV
    const handleExport = useCallback(() => {
        if (!rows.length) {
            toast.info('No data to export');
            return;
        }

        const headers = [
            'UIN',
            'Activity',
            'LawArea',
            'Status',
            'DueDate',
            'ActOrRule',
            'Owners',
            'Managers',
        ];

        const csvRows = rows.map((row) => {
            const owners = Array.isArray(row?.trackerOwner)
                ? row.trackerOwner
                    .map((o) =>
                        typeof o === 'object'
                            ? o.name || o.email || 'N/A'
                            : o
                    )
                    .join('; ')
                : row?.trackerOwner || '';

            const managers = Array.isArray(row?.trackerManager)
                ? row.trackerManager
                    .map((m) =>
                        typeof m === 'object'
                            ? m.email || m.name || 'N/A'
                            : m
                    )
                    .join('; ')
                : row?.trackerManager || '';

            const dueDate = row.dueDate
                ? new Date(row.dueDate).toLocaleDateString()
                : '';

            const values = [
                row.uniqueIdentifier || '',
                row.complianceActivity || '',
                row.lawArea || '',
                row.status || '',
                dueDate,
                row.actOrRule || '',
                owners,
                managers,
            ];

            return values
                .map((v) =>
                    `"${String(v).replace(/"/g, '""')}"`
                )
                .join(',');
        });

        const csvContent = [headers.join(','), ...csvRows].join('\n');
        const blob = new Blob([csvContent], {
            type: 'text/csv;charset=utf-8;',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const fileName = `assignments_${new Date()
            .toISOString()
            .slice(0, 10)}.csv`;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [rows]);

    // Submit the "assignment box" data
    const handleAssignmentSubmit = useCallback(async () => {
        try {
            const selectedUINs = rows
                .filter(
                    (r) =>
                        selectedTrackers.includes(
                            r._id || r.uniqueIdentifier
                        ) && isEditableRowForCurrentUser(r)
                )
                .map((r) => r.uniqueIdentifier);

            if (!selectedUINs.length) {
                toast.info('Select at least one editable tracker.');
                return;
            }

            const payload = {
                Action: action,
                Role: role,
                User: assignUsers,
                UIN: selectedUINs,
            };

            const { data } = await api.post(
                'tracker/assignment-trackers',
                payload
            );

            if (data.success) {
                toast.success(
                    data.message || 'Assignment updated successfully'
                );
                setOperationId(data.operationId);
                setChangedTrackers(data.updatedTrackers || []);
                setShowChanges(true);
                setSelectedTrackers([]);
                setAssignUsers([]);
                handleSubmit();
            } else {
                toast.error(data.message || 'Error updating assignment');
            }
        } catch (err) {
            console.error(err);
            const errorMessage =
                err.response?.data?.message ||
                'Error updating assignment';
            toast.error(errorMessage);
        }
    }, [
        action,
        role,
        assignUsers,
        rows,
        selectedTrackers,
        handleSubmit,
        isEditableRowForCurrentUser,
    ]);

    // ---------------------------
    // ROLLBACK LOGIC
    // ---------------------------
    const handleRollbackSelect = useCallback((uin) => {
        setRollbackSelection((prev) => {
            if (prev.includes(uin)) {
                return prev.filter((x) => x !== uin);
            }
            return [...prev, uin];
        });
    }, []);

    const handleRollbackAll = useCallback(() => {
        setRollbackSelection((prev) => {
            if (prev.length === changedTrackers.length) {
                return [];
            }
            const allUINs = changedTrackers.map(
                (t) => t.uniqueIdentifier
            );
            return allUINs;
        });
    }, [changedTrackers]);

    const handleRollbackRequest = useCallback(async () => {
        try {
            await api.post('tracker/rollback-trackers', {
                operationId,
                uniqueIdentifiers: rollbackSelection,
            });
            toast.success('Rollback successful');
            // refresh data with same filters
            handleSubmit();
            setShowChanges(false);
            setOperationId(null);
            setChangedTrackers([]);
            setRollbackSelection([]);
        } catch (err) {
            console.error(err);
            toast.error('Error rolling back changes');
        }
    }, [operationId, rollbackSelection, handleSubmit]);

    const handleRowsPerPageChange = useCallback((e) => {
        setRowsPerPage(Number(e.target.value));
        setCurrentPage(1);
    }, []);

    const handlePageChange = useCallback((_, value) => {
        setCurrentPage(value);
    }, []);

    return (
        <Layout title="Tracker Assignments">
            <Container maxWidth="lg" sx={{ mb: 2 }}>
                {/* Header */}
                <Box sx={{ mt: 3, mb: 2 }}>
                    <Typography variant="h4" gutterBottom>
                        Manage Assignments
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: -0.5 }}
                    >
                        Filter trackers, update assignments, and safely
                        rollback changes.
                    </Typography>
                </Box>

                {/* Toggle between Basic and Advanced */}
                <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={view === 'Advanced'}
                                onChange={handleViewChange}
                                size="small"
                            />
                        }
                        label="Advanced filters"
                    />
                </Box>

                {/* Filter Form */}
                <AssignmentFilters
                    view={view}
                    company={company}
                    companies={companies}
                    location={location}
                    locations={locations}
                    user={user}
                    users={users}
                    lawArea={selectedLawAreas}
                    lawAreas={lawAreas}
                    roles={selectedRoles}
                    roleOptions={ROLE_OPTIONS}
                    periodicities={selectedPeriodicities}
                    periodicityOptions={PERIODICITY_OPTIONS}
                    category={category}
                    activity={activity}
                    actOrRule={actOrRule}
                    startDueDate={startDueDate}
                    endDueDate={endDueDate}
                    includeClosed={includeClosed}
                    onCompanyChange={handleCompanyChange}
                    onLocationChange={handleLocationChange}
                    onUserChange={handleUserChange}
                    onLawAreaChange={handleLawAreaChange}
                    onRolesChange={handleRolesFilterChange}
                    onPeriodicitiesChange={handlePeriodicitiesChange}
                    onCategoryChange={(e) =>
                        setCategory(e.target.value)
                    }
                    onActivityChange={(e) =>
                        setActivity(e.target.value)
                    }
                    onActOrRuleChange={(e) =>
                        setActOrRule(e.target.value)
                    }
                    onStartDueDateChange={(e) =>
                        setStartDueDate(e.target.value)
                    }
                    onEndDueDateChange={(e) =>
                        setEndDueDate(e.target.value)
                    }
                    onIncludeClosedChange={handleIncludeClosedChange}
                    onSubmit={handleSubmit}
                    onClear={handleClear}
                />

                {/* Action Buttons (Refresh / Export / Select All) */}
                <AssignmentActionsBar
                    onReload={handleReload}
                    onSelectAll={handleSelectAll}
                    onExport={handleExport}
                    hasRows={rows.length > 0}
                />

                {/* Table */}
                <TrackersTable
                    rows={rows}
                    displayedRows={displayedRows}
                    selectedTrackers={selectedTrackers}
                    onRowSelect={handleRowSelect}
                    onToggleSelectAll={handleSelectAll}
                    allSelected={allSelected}
                    someSelected={someSelected}
                    isAdmin={isAdmin}
                />

                {/* Pagination & Rows per Page Selector */}
                <PaginationControls
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                />

                {/* ASSIGNMENT BOX */}
                <AssignmentUpdateBox
                    hasSelection={selectedTrackers.length > 0}
                    selectionCount={selectedTrackers.length}
                    action={action}
                    role={role}
                    assignUsers={assignUsers}
                    users={users}
                    onActionChange={setAction}
                    onRoleChange={setRole}
                    onAssignUsersChange={setAssignUsers}
                    onSubmit={handleAssignmentSubmit}
                />

                {/* DIALOG: changed trackers */}
                <ChangedTrackersDialog
                    open={showChanges}
                    changedTrackers={changedTrackers}
                    rollbackSelection={rollbackSelection}
                    onClose={() => setShowChanges(false)}
                    onRollbackAll={handleRollbackAll}
                    onRollbackSelect={handleRollbackSelect}
                    onRollbackRequest={handleRollbackRequest}
                />
            </Container>
        </Layout>
    );
};

export default AssignmentPage;
